import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import { UsersService } from '../users/users.service';

@Injectable({
  providedIn: 'root'
})
export class TeamsService {

  constructor(
    private restangular: Restangular,
    private usersService: UsersService,
  ) {
  }

  getUserMemberTeams(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).one('member').getList().toPromise();
  }

  getUserTeamInfo(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).get().toPromise();
  }

  getTeamInfo(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).get().toPromise();
  }

  getCategories(): Promise<any> {
    return this.restangular.one('teams').one('categories').get().toPromise();
  }

  getTeamMembers(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('members').getList().toPromise();
  }

  addTeamMember(teamId: string, userEmail, userId = null): Promise<any> {
    return this.restangular.one('teams', teamId).one('member').customPOST({
      userId: userId,
      userEmail: userEmail,
      balance: 0
    }).toPromise();

  }

  getCompanyForTeam(teamId: string): Promise<any> {
    return this.restangular.one('teams', 'companies').one('team', teamId).getList().toPromise();
  }

  removeTeamMember(id: string): Promise<any> {
    return this.restangular.one('teams').one('member', id).remove().toPromise();
  }

  createTeam(userId: string, name: string): Promise<any> {
    return this.restangular.one('teams', userId).customPOST({
      name,
      categories: [],
      frequency: 'monthly',
      amount: 0,
      disabled: false,
      plan: 'Free'
    }).toPromise();
  }

  modifyTeam(teamId: string, fields: any): Promise<any> {
    return this.restangular.one('teams', teamId).customPUT({
      ...fields
    }).toPromise();
  }

  modifyTeamMember(teamId: string, memberId: string, fields: any): Promise<any> {
    return this.restangular.one('teams', teamId).one('member', memberId).customPUT({
      ...fields
    }).toPromise();
  }

  deleteTeam(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).remove().toPromise();
  }

  resendEmailToTeamMember(teamId: string): Promise<any> {
    return this.restangular.one('teams').one('member', teamId).one('resend').customPUT().toPromise();
  }

  setStripeSessionId(userId: string, sessionId: string): Promise<any> {
    return this.getUserTeamInfo(userId).then(info => {
      return this.restangular.one('teams', info.id).customPUT({
        stripeSessionId: sessionId
      });
    });
  }

  makeRecognitionTransfer(teamId: string, userId: string, amount: number, members: any, message: string): Promise<any> {
    return this.restangular.one('teams', teamId).one(userId).one('makerecognitiontransfer').customPOST({
      amount,
      members,
      message
    }).toPromise();
  }

  moveP2pTransfer(teamId: string, userId: string, amount: number): Promise<any> {
    return this.restangular.one('teams', teamId).one(userId).one('movep2ptransfer').customPOST({ amount }).toPromise();
  }

  uploadMemberList(teamId, data): Promise<any> {
    return this.restangular.one('teams', teamId).one('uploadmembers').customPUT(data, undefined, undefined, { 'content-type': 'text/html' }).toPromise();
  }

  buildMemberList(members: Array<any>, team: any): Array<any> {
    const newMembers: Array<any> = [];
    members.forEach((member: any, index: number) => {
      if (!member.userId || !member.status) {
        if (member.status !== 'active' && team.amount !== 0) {
          newMembers.push(member.email);
        }
      }
      if (member.userId) {
        let me = false;
        if (member.userId === this.usersService.getCurrentUserId()) {
          if (!member.lastName) {
            member.lastName = '';
          }
          if (member.userId === team.ownerUserId) {
            member.lastName += ' (Admin)';
          }
          member.lastName += ' (Me)';
          me = true;
        }
        if (me) {
          const thisMember = members[index];
          members.splice(index, 1);
          members.unshift(thisMember);
        }
      } else {
        member.avatar = 'no_avatar.png';
      }
    });
    return newMembers;
  }

  savePurchase(teamId: string, userId: string, purchase: any): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases').customPOST(purchase).toPromise();
  }

  updatePurchase(teamId: string, userId: string, purchaseId: string, purchase: any): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).customPUT(purchase).toPromise();
  }

  removePurchase(teamId: string, userId: string, purchaseId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).remove().toPromise();
  }

  setReceipt(teamId: string, userId: string, purchaseId: string, img: any): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).all('receipt').customPUT(img, undefined, undefined, { 'content-type': 'image/*' }).toPromise();
  }

  getPurchases(teamId: string, userId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases').getList().toPromise();
  }

  getPurchase(teamId: string, userId: string, purchaseId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).get().toPromise();
  }

  getActivity(userId: string): Promise<any> {
    // MM-3417: "Reimbursement" transactions were filtered out in the backend, but this caused 
    // unexpected behavior in team-admin.
    // MM-3876: The backend was updated to support `includeStatus` and `excludeStatus` query params
    // that accept comma separated statuses.
    return this.restangular.one('teams').one('user', userId).one('activity').customGET('', { excludeStatus: 'Reimbursement', messageBuild: true }).toPromise();
  }

  getP2pActivity(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('p2prewards').getList().toPromise();
  }

  getP2PHistory(teamId: string, userId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('p2phistory').getList().toPromise();
  }

}
